import cn from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import ButtonGeneral from 'students/views/shared/components/ButtonGeneral';
import LinkElem from 'students/views/shared/components/LinkElem';
import { Spinner } from 'students/views/shared/components/Spinner';
import UrlIcon from 'students/views/shared/components/UrlIcon';
import styled from 'styled-components';
import WaveSurfer, { WaveSurferOptions } from 'wavesurfer.js';
import { captureMediaLoadError } from './media/captureMediaLoadError';
import { isAbortError, isNotSupportedError } from './media/isAbortError';
import DownloadIcon from '../assets/download.svg';
import { seconds2timestring } from '../utils/seconds2timestring';
import pauseIcon from './assets/pause_icon.svg';
import playIcon from './assets/play_icon.svg';

interface IProps {
  url: string;
  size?: number;
  customOptions?: WaveSurferOptions;
  isDownloadable: boolean;
  id: string;
  className?: string;
}

enum AudioState {
  Initial,
  Playing,
  Pausing
}

/** Report a refused `play()`, except the rejections that are races rather than broken
 *  assets: `NotSupportedError` (no `src` yet — the tap beat the decode) and `AbortError`
 *  (instance destroyed mid-play). */
function playbackErrorHandler(url: string) {
  return (error: unknown): void => {
    if (isAbortError(error) || isNotSupportedError(error)) return;
    captureMediaLoadError(error, { kind: 'audio', engine: 'wavesurfer', src: url });
  };
}

const WaveAudioPlayer: React.FC<IProps> = ({
  url,
  size,
  isDownloadable = true,
  customOptions,
  id,
  className
}) => {
  const [state, setState] = useState<AudioState>(AudioState.Initial);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  /** WaveSurfer assigns the media `src` only once its own fetch+decode finished. */
  const [isReady, setIsReady] = useState(false);
  const [isPlayPending, setIsPlayPending] = useState(false);
  const waveform = useRef<WaveSurfer | null>(null);
  const intervalRef = useRef<number | null>(null);
  const playIntentRef = useRef(false);
  /** Set once this instance's load has failed: `ready` never fires afterwards and nothing
   *  re-issues `load()`, so a tap must not be queued as pending (see `handlePlay`). */
  const loadFailedRef = useRef(false);

  // Read via a ref, not a dep: a fresh object literal from a parent must not destroy and
  // recreate WaveSurfer on every render (that killed playback mid-stream).
  const customOptionsRef = useRef(customOptions);
  customOptionsRef.current = customOptions;

  useEffect(() => {
    // Nothing may carry over from the previous url/id, or the icon and the 1s timer keep
    // claiming playback that was destroyed with the old instance.
    setState(AudioState.Initial);
    setCurrentTime(0);
    setDuration(0);
    setIsReady(false);
    setIsPlayPending(false);
    playIntentRef.current = false;
    loadFailedRef.current = false;

    const ws = WaveSurfer.create({
      barWidth: 1,
      barGap: 2,
      minPxPerSec: 100,
      cursorWidth: 1,
      container: `#waveform_${id}`,
      height: 60,
      progressColor: '#00A5D7',
      waveColor: 'var(--sub-text-color)',
      cursorColor: 'transparent',
      ...customOptionsRef.current
    });
    waveform.current = ws;

    ws.on('error', function (error) {
      // Release a queued tap before any early return: `ready` never fires for a load that
      // errored, so a pending spinner left here spins forever. Both orderings matter: the
      // tap can land before the error (release it) or after (`handlePlay` reads the ref).
      if (waveform.current === ws) {
        loadFailedRef.current = true;
        playIntentRef.current = false;
        setIsPlayPending(false);
      }
      // `destroy()` aborts the fetch controller, so leaving the screen mid-download
      // surfaces an AbortError here for a perfectly healthy URL.
      if (isAbortError(error)) return;
      captureMediaLoadError(error, { kind: 'audio', engine: 'wavesurfer', src: url });
    });
    ws.on('ready', function () {
      if (waveform.current !== ws) return;
      setDuration(ws.getDuration());
      setIsReady(true);
      if (playIntentRef.current) {
        playIntentRef.current = false;
        setIsPlayPending(false);
        Promise.resolve(ws.play()).catch(playbackErrorHandler(url));
      }
    });
    ws.on('seeking', function () {
      setCurrentTime(ws.getCurrentTime());
    });
    ws.on('finish', function () {
      setState(AudioState.Pausing);
    });
    // Drive the play/pause state from what WaveSurfer actually did, so a rejected or
    // ignored playPause() cannot leave the UI showing a pause icon over silence.
    ws.on('play', function () {
      if (waveform.current === ws) setState(AudioState.Playing);
    });
    ws.on('pause', function () {
      if (waveform.current === ws) {
        setState(AudioState.Pausing);
        setCurrentTime(ws.getCurrentTime());
      }
    });

    // Subscriptions first, then the download: `load()` re-throws what it already emitted
    // on 'error' (including the AbortError from `destroy()`), so the rejection is swallowed
    // here and reporting stays in the 'error' handler above.
    Promise.resolve(ws.load(url)).catch(() => {});

    return () => {
      if (waveform.current === ws) waveform.current = null;
      ws.destroy();
    };
    // customOptions is intentionally read via ref, not listed as a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, id]);

  useEffect(() => {
    if (state === AudioState.Playing) {
      intervalRef.current = window.setInterval(() => {
        if (waveform.current) setCurrentTime(waveform.current.getCurrentTime());
      }, 1000);
    } else {
      intervalRef?.current && clearInterval(intervalRef.current);
    }

    return () => {
      intervalRef.current && clearInterval(intervalRef.current);
    };
  }, [state]);

  const handlePlay = useCallback(() => {
    const ws = waveform.current;
    if (!ws) return;

    if (!isReady) {
      // A load that already failed never becomes ready, so queuing here would show a
      // spinner nothing can clear.
      if (loadFailedRef.current) return;

      // Nothing is decoded yet, so playPause() would hit a source-less media element and
      // reject with NotSupportedError. Queue the intent instead; 'ready' starts playback.
      playIntentRef.current = !playIntentRef.current;
      setIsPlayPending(playIntentRef.current);
      return;
    }

    // No optimistic state change: the 'play'/'pause' subscriptions are the single source
    // of truth, so a play() the browser refuses leaves the UI on the play icon.
    Promise.resolve(ws.playPause()).catch(playbackErrorHandler(url));
  }, [isReady, url]);

  return (
    <SWaveformContainer className={cn(className)}>
      <SIconButton onClick={handlePlay}>
        {isPlayPending ? (
          <Spinner variant="darkBlue" />
        ) : (
          <UrlIcon
            url={
              state === AudioState.Initial || state === AudioState.Pausing
                ? playIcon
                : pauseIcon
            }
            height="38px"
            width="38px"
          />
        )}
      </SIconButton>

      <SWave id={`waveform_${id}`} />

      {state === AudioState.Initial ? (
        <STimeContainer>
          {seconds2timestring(duration)}
          {size ? <SAudioSize>{`${size} MB`}</SAudioSize> : null}
        </STimeContainer>
      ) : (
        <STimeContainer>
          {`${seconds2timestring(currentTime)} / ${seconds2timestring(duration)}`}
        </STimeContainer>
      )}

      {isDownloadable && (
        <SAudioLinkContainer>
          <LinkElem aria-label="Download" to={url}>
            <img src={DownloadIcon} alt="download" />
          </LinkElem>
        </SAudioLinkContainer>
      )}
    </SWaveformContainer>
  );
};

export default WaveAudioPlayer;

const SWave = styled.div`
  width: 100%;
  height: 30px;
  margin-block-start: k-start: -30px;
  overflow: hidden;
`;

const SWaveformContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  position: relative;
  height: 60px;
  width: 372px;
  background: rgba(240, 240, 243, 0.5);
  border-radius: 6px;
`;

const SIconButton = styled(ButtonGeneral)`
  background: var(--wave-audio-player-icon-background);
  border-radius: 50%;
  margin-inline-start: 14px;
  margin-inline-end: 8px;
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const STimeContainer = styled.div`
  position: absolute;
  bottom: 10px;
  left: 65px;
  font-size: 12px;
  line-height: 14px;
  color: var(--sub-text-color);
`;

const SAudioSize = styled.span`
  margin-inline-start: 8px;
`;

const SAudioLinkContainer = styled.div`
  width: 44px;
  height: 32px;
  background: #00a5d7;
  background: rgba(0, 165, 215, 0.1);
  border-radius: 4px;
  margin-inline-end: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
